import type { MatchRecord, PlayerStats, RoomSummary, SessionResponse } from '@sw/protocol';
import { SERVER_URL } from '../config';
import { useSession } from '../stores/session';

export class ApiError extends Error {
  constructor(
    public code: string,
    public status: number,
  ) {
    super(code);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = useSession.getState().token;
  let res: Response;
  try {
    res = await fetch(`${SERVER_URL}${path}`, {
      ...init,
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError('NETWORK', 0);
  }
  const body = (await res.json().catch(() => ({}))) as { error?: { code?: string } };
  if (!res.ok) throw new ApiError(body.error?.code ?? 'INTERNAL', res.status);
  return body as T;
}

export const api = {
  createSession: (username: string, takeover = false) =>
    request<SessionResponse>('/api/session', { method: 'POST', body: JSON.stringify({ username, takeover }) }),
  deleteSession: () => request<{ ok: true }>('/api/session', { method: 'DELETE' }),
  room: (code: string) => request<RoomSummary>(`/api/rooms/${encodeURIComponent(code)}`),
  stats: (username: string) => request<PlayerStats>(`/api/players/${encodeURIComponent(username)}/stats`),
  matches: (username: string, before?: number) =>
    request<{ matches: MatchRecord[] }>(
      `/api/players/${encodeURIComponent(username)}/matches${before ? `?before=${before}` : ''}`,
    ),
  match: (id: string) => request<MatchRecord>(`/api/matches/${encodeURIComponent(id)}`),
};
